import { featureCollection, type LineString } from '@turf/helpers';
import { useMemo } from 'react';
import getLineStrings from '@alltrails/maps/components/CustomRoute/Segments/getLineStrings';
import DistanceMarkers from '@alltrails/maps/components/DistanceMarkers/DistanceMarkers';
import PolylineSource from '@alltrails/maps/components/Polyline/PolylineSource';
import StartEndMarkers from '@alltrails/maps/components/StartEndMarker/StartEndMarkers';
import type { SegmentPolyline } from '@alltrails/maps/types/CustomRoute';
import type { LngLat } from '@alltrails/maps/types/Geo';
import type { LineProperties } from '@alltrails/types/CustomRoute';
import { useAppSelector } from '../../redux';
import type { PrintRouteWaypoint, PrintWaypointVariant } from '../../types/PrintRouteWaypoint';
import PrintCuratedWaypointPins from './PrintCuratedWaypointPins';
import PrintWaypointPins from './PrintWaypointPins';

type PrintRouteContentProps = {
  /**
   * The lines the route draws, in draw order, each carrying its own color and paths. Hosts adapt
   * their own state into this: a custom route, a recording and a trail all pass the segments off
   * the customRoute slice, which the page behind the print has already hydrated.
   */
  polylines?: SegmentPolyline[];
  waypoints?: PrintRouteWaypoint[];
  waypointVariant?: PrintWaypointVariant;
};

const EMPTY_POLYLINES: SegmentPolyline[] = [];
const EMPTY_WAYPOINTS: PrintRouteWaypoint[] = [];

const toLngLat = ([lat, lng]: [number, number]): LngLat => [lng, lat];

/**
 * What a route puts on the printed map, whichever surface it was opened from. Insertion order is
 * z-order: the route sits over the host's overlays, the pins over the route, as on the live map.
 * Lines are styled through getLineStrings with hasSourcePaint, as InactivePolylines does, so a
 * multi-colored route keeps its own colors.
 */
const PrintRouteContent = ({ polylines = EMPTY_POLYLINES, waypoints = EMPTY_WAYPOINTS, waypointVariant = 'user' }: PrintRouteContentProps) => {
  const distanceMarkersEnabled = useAppSelector(state => state.map.dynamicStyleIds.includes('distanceMarkers'));

  const routeData = useMemo(
    () => featureCollection<LineString, LineProperties>(polylines.flatMap((polyline, index) => getLineStrings(polyline, undefined, index))),
    [polylines]
  );

  const { coordinates, markerPolylines } = useMemo(() => {
    // One entry per line, holding the points of the paths long enough to draw: a single-point path
    // is no part of the drawn line, so it is no part of what the markers read either
    const lngLatPolylines = polylines.map<LngLat[]>(polyline =>
      polyline.paths.filter(path => path.points.length > 1).flatMap(path => path.points.map(toLngLat))
    );

    return {
      // The endpoint markers read the first and last point of the whole route
      coordinates: lngLatPolylines.flat(),
      // The distance markers count up from 1 within each line and reset between them, so a route
      // with a gap doesn't measure across it
      markerPolylines: lngLatPolylines.filter(polyline => polyline.length > 1)
    };
  }, [polylines]);

  return (
    <>
      {routeData.features.length > 0 && <PolylineSource data={routeData} hasSourcePaint uniqueId="print-route" />}
      {distanceMarkersEnabled && <DistanceMarkers polylines={markerPolylines} />}
      <StartEndMarkers coordinates={coordinates} />
      {waypointVariant === 'curated' && <PrintCuratedWaypointPins waypoints={waypoints} />}
      {waypointVariant === 'user' && <PrintWaypointPins waypoints={waypoints} />}
    </>
  );
};

export default PrintRouteContent;
